// Procedural Biomes and Map Definitions for WindShot PvE

export const BIOMES = {
  meadow: {
    id: 'meadow',
    name: 'Windy Hills',
    icon: '🌿',
    sky: '#86d7ff',
    ground: '#5ca64b',
    soil: '#7a5531',
    windRange: [0.6, 1.4],
    gravity: 1.0,
    topographies: ['rolling', 'twin_peaks'],
    nameAdjectives: ['Breezy', 'Emerald', 'Verdant', 'Whispering', 'Rolling', 'Sunlit'],
    nameNouns: ['Hills', 'Meadow', 'Heights', 'Ridge', 'Knoll', 'Pasture']
  },
  desert: {
    id: 'desert',
    name: 'Howling Desert',
    icon: '🏜️',
    sky: '#ffd59b',
    ground: '#d6a647',
    soil: '#9c6a2d',
    windRange: [0.9, 1.9],
    gravity: 1.0,
    topographies: ['dunes', 'plateau', 'canyon'],
    nameAdjectives: ['Howling', 'Golden', 'Scorched', 'Shifting', 'Dusty', 'Arid'],
    nameNouns: ['Dunes', 'Desert', 'Badlands', 'Canyon', 'Wastes', 'Plateau']
  },
  canyon: {
    id: 'canyon',
    name: 'Redwood Ravine',
    icon: '🌲',
    sky: '#93c5fd',
    ground: '#65a30d',
    soil: '#78350f',
    windRange: [0.8, 1.6],
    gravity: 1.0,
    topographies: ['canyon', 'twin_peaks'],
    nameAdjectives: ['Redwood', 'Deep', 'Shattered', 'Craggy', 'Shadowed', 'Echoing'],
    nameNouns: ['Ravine', 'Gorge', 'Chasm', 'Pass', 'Canyon', 'Rift']
  },
  ice: {
    id: 'ice',
    name: 'Frozen Valley',
    icon: '❄️',
    sky: '#aee9ff',
    ground: '#c5f4ff',
    soil: '#78b8d5',
    windRange: [1.1, 2.2],
    gravity: 1.0,
    topographies: ['crags', 'rolling', 'twin_peaks'],
    nameAdjectives: ['Frozen', 'Glacial', 'Biting', 'Crystal', 'Shivering', 'Frost'],
    nameNouns: ['Valley', 'Peaks', 'Glacier', 'Pass', 'Spire', 'Tundra']
  },
  volcano: {
    id: 'volcano',
    name: 'Volcano Peak',
    icon: '🌋',
    sky: '#6f4b5b',
    ground: '#4d403e',
    soil: '#2e2524',
    windRange: [1.0, 2.0],
    gravity: 1.05,
    topographies: ['crater', 'crags'],
    nameAdjectives: ['Volcanic', 'Molten', 'Smoldering', 'Obsidian', 'Infernal', 'Ash'],
    nameNouns: ['Peak', 'Crater', 'Caldera', 'Basin', 'Ridge', 'Fissure']
  },
  moon: {
    id: 'moon',
    name: 'Crescent Moon',
    icon: '🌙',
    sky: '#1f2a55',
    ground: '#9aa3b4',
    soil: '#596071',
    windRange: [1.2, 2.4],
    gravity: 0.72,
    topographies: ['crater', 'rolling', 'canyon'],
    nameAdjectives: ['Lunar', 'Crescent', 'Celestial', 'Starlit', 'Silent', 'Orbital'],
    nameNouns: ['Crater', 'Basin', 'Sea', 'Plateau', 'Rift', 'Highlands']
  },
  citadel: {
    id: 'citadel',
    name: 'Storm Citadel',
    icon: '⚡',
    sky: '#526b9e',
    ground: '#5b6b66',
    soil: '#353f3e',
    windRange: [1.4, 2.7],
    gravity: 0.95,
    topographies: ['twin_peaks', 'plateau', 'canyon'],
    nameAdjectives: ['Storm', 'Tempest', 'Thunder', 'Iron', 'Gale', 'Vanguard'],
    nameNouns: ['Citadel', 'Spires', 'Bastion', 'Fortress', 'Sanctuary', 'Heights']
  }
};

/**
 * Generate a procedural, fully randomized map with unique topography and physics.
 */
export function generateProceduralMap({
  biomeKey = 'random',
  difficultyKey = 'normal',
  seed = Math.random(),
  hasBoss = null
} = {}) {
  const biomeKeys = Object.keys(BIOMES);
  const selectedBiomeKey = biomeKey === 'random' || !BIOMES[biomeKey]
    ? biomeKeys[Math.floor(Math.random() * biomeKeys.length)]
    : biomeKey;

  const biome = BIOMES[selectedBiomeKey];
  const adj = biome.nameAdjectives[Math.floor(Math.random() * biome.nameAdjectives.length)];
  const noun = biome.nameNouns[Math.floor(Math.random() * biome.nameNouns.length)];
  const name = `${adj} ${noun}`;

  const topoList = biome.topographies || ['rolling', 'canyon', 'twin_peaks'];
  const topography = topoList[Math.floor(Math.random() * topoList.length)];

  // Determine enemy count and boss status
  const isBossMap = hasBoss !== null ? hasBoss : (Math.random() < 0.45 || selectedBiomeKey === 'volcano' || selectedBiomeKey === 'citadel');
  const enemiesCount = isBossMap ? (Math.random() < 0.5 ? 3 : 4) : (Math.random() < 0.4 ? 2 : 3);

  // Generate varied wind strength within biome parameters
  const [minWind, maxWind] = biome.windRange;
  const windFactor = Number((minWind + Math.random() * (maxWind - minWind)).toFixed(2));

  // Generate varied harmonic parameters based on topography
  let base = 0.72 + (Math.random() - 0.5) * 0.06;
  let amp1 = 0.06 + Math.random() * 0.05;
  let freq1 = 1.6 + Math.random() * 1.5;
  let amp2 = 0.02 + Math.random() * 0.035;
  let freq2 = 4.2 + Math.random() * 3.5;
  const phase = Math.random() * Math.PI * 2;

  if (topography === 'canyon') {
    amp1 = 0.08 + Math.random() * 0.04;
    freq1 = 1.3 + Math.random() * 0.8;
  } else if (topography === 'twin_peaks') {
    amp1 = 0.09 + Math.random() * 0.04;
    freq1 = 2.0 + Math.random() * 0.6;
  }

  const uniqueId = `proc-${selectedBiomeKey}-${Math.floor(Math.random() * 9000 + 1000)}`;

  return {
    id: uniqueId,
    name,
    icon: biome.icon,
    isProcedural: true,
    biomeKey: selectedBiomeKey,
    sky: biome.sky,
    ground: biome.ground,
    soil: biome.soil,
    wind: windFactor,
    gravity: biome.gravity || 1.0,
    enemies: enemiesCount,
    boss: isBossMap,
    terrain: {
      base,
      amp1,
      freq1,
      amp2,
      freq2,
      phase,
      topography,
      seed
    }
  };
}

export const MAPS = [
  {
    id: 'meadow', name: 'Windy Hills', icon: '🌿', unlock: 0,
    sky: '#86d7ff', ground: '#5ca64b', soil: '#7a5531', wind: 0.9,
    terrain: { base: 0.72, amp1: 0.06, freq1: 1.9, amp2: 0.035, freq2: 4.6, phase: 0.2, topography: 'rolling' },
    enemies: 2, boss: false
  },
  {
    id: 'desert', name: 'Howling Desert', icon: '🏜️', unlock: 1,
    sky: '#ffd59b', ground: '#d6a647', soil: '#9c6a2d', wind: 1.3,
    terrain: { base: 0.75, amp1: 0.06, freq1: 2.7, amp2: 0.025, freq2: 5.4, phase: 1.0, topography: 'dunes' },
    enemies: 3, boss: false
  },
  {
    id: 'canyon', name: 'Redwood Ravine', icon: '🌲', unlock: 2,
    sky: '#93c5fd', ground: '#65a30d', soil: '#78350f', wind: 1.4,
    terrain: { base: 0.73, amp1: 0.08, freq1: 1.7, amp2: 0.03, freq2: 5.8, phase: 0.5, topography: 'canyon' },
    enemies: 3, boss: false
  },
  {
    id: 'ice', name: 'Frozen Valley', icon: '❄️', unlock: 3,
    sky: '#aee9ff', ground: '#c5f4ff', soil: '#78b8d5', wind: 1.6,
    terrain: { base: 0.70, amp1: 0.09, freq1: 2.1, amp2: 0.03, freq2: 7.0, phase: 0.8, topography: 'crags' },
    enemies: 3, boss: false
  },
  {
    id: 'volcano', name: 'Volcano Peak', icon: '🌋', unlock: 4,
    sky: '#6f4b5b', ground: '#4d403e', soil: '#2e2524', wind: 1.5,
    terrain: { base: 0.76, amp1: 0.08, freq1: 3.2, amp2: 0.04, freq2: 6.0, phase: 2.1, topography: 'crater' },
    enemies: 3, boss: true, gravity: 1.05
  },
  {
    id: 'moon', name: 'Crescent Moon', icon: '🌙', unlock: 5,
    sky: '#1f2a55', ground: '#9aa3b4', soil: '#596071', wind: 1.9,
    terrain: { base: 0.74, amp1: 0.07, freq1: 2.6, amp2: 0.045, freq2: 8.5, phase: 0.4, topography: 'rolling' },
    enemies: 4, boss: false, gravity: 0.72
  },
  {
    id: 'citadel', name: 'Storm Citadel', icon: '⚡', unlock: 6,
    sky: '#526b9e', ground: '#5b6b66', soil: '#353f3e', wind: 2.3,
    terrain: { base: 0.71, amp1: 0.10, freq1: 2.0, amp2: 0.045, freq2: 5.2, phase: 1.7, topography: 'twin_peaks' },
    enemies: 4, boss: true
  }
];

export const DIFFICULTIES = {
  easy: {
    key: 'easy',
    name: 'Easy',
    badge: '🌱 Casual',
    hp: 0.8,
    enemyDamage: 0.75,
    aiError: 0.28,
    windComp: 0.4,
    wind: 0.75,
    reward: 1.0,
    allowBossSpecial: false,
    description: 'Gentle breeze, forgiving enemy aim, lower damage.'
  },
  normal: {
    key: 'normal',
    name: 'Normal',
    badge: '⚔️ Balanced',
    hp: 1.0,
    enemyDamage: 1.0,
    aiError: 0.12,
    windComp: 0.82,
    wind: 1.1,
    reward: 1.5,
    allowBossSpecial: true,
    description: 'Competitive physics, smart enemies that adjust to wind.'
  },
  hard: {
    key: 'hard',
    name: 'Hard',
    badge: '🔥 Veteran',
    hp: 1.35,
    enemyDamage: 1.35,
    aiError: 0.05,
    windComp: 0.96,
    wind: 1.55,
    reward: 2.3,
    allowBossSpecial: true,
    description: 'Heavy winds, sharp enemy ballistics, high threat.'
  },
  expert: {
    key: 'expert',
    name: 'Wind Master',
    badge: '👑 Extreme',
    hp: 1.65,
    enemyDamage: 1.6,
    aiError: 0.02,
    windComp: 1.0,
    wind: 2.2,
    reward: 3.5,
    allowBossSpecial: true,
    description: 'Gale-force storms, sniper AI accuracy, enraging Bosses!'
  }
};
